import React, { useState, useRef, useEffect } from 'react'
import { useStaticQuery, graphql } from 'gatsby'

import { useForm, Controller, FormProvider } from 'react-hook-form'
import PriceComparison from '../PriceComparison'
import RichText from '../RichText'
import { LINEVISITATION_ENDPOINT } from '../../utils/constants'
import AutocompleteBase from '../Form/Fields/AutocompleteBase/AutocompleteBase'
import { FieldType } from '../Form/utils/fieldTypeEnum'
import { ILinevisitation, IAddress } from './types'
import { useLocation } from '@reach/router'

// MUI
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'

// ContactForm
import { styled, useTheme } from '@mui/system'
import { IProduct } from '../PriceComparison/types'

enum Views {
  SEARCH = 'SEARCH',
  RESULTS = 'RESULTS',
  LOADING = 'LOADING',
  NORESULTS = 'NORESULTS',
}

enum AddressTypes {
  VOK = 'VOK',
  HAP = 'HAP',
}

export type Technology = 'FIBER' | 'FIBER PLUS' | 'COAX' | 'MOBILE_BB'

interface ILinevisitationProduct {
  technology: Technology
  price: number
  speed: string
}

interface AddressFormInput {
  dawa: string
}

type Resource = {
  title: string
  content: {
    content: string
  }
}

type DisclaimerTexts = {
  HAP: {
    resources: Resource[]
  }
  VOK: {
    resources: Resource[]
  }
}

type formattedDisclaimerTexts = {
  [key: string]: {
    [title: string]: string
  }
}

const Linevisitation = ({
  priceComparisonModule,
  resultsTitle,
  searchTitle,
  noofSlide,
  searchFieldLabel,
  noResultsExplanation,
  isHap,
}: ILinevisitation) => {
  const { VOK, HAP } = AddressTypes
  const { SEARCH, RESULTS, LOADING, NORESULTS } = Views
  const [view, setView] = useState(SEARCH)
  const [excludeSimilar, setExcludeSimilar] = useState(false)
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const [addressType, _setAddressType] = useState(isHap ? HAP : VOK)
  const [_address, setAddress] = useState<IAddress | null>(null)
  const [products, setProducts] = useState<IProduct[]>([])
  const location = useLocation()
  const [dawaValue, setDawaValue] = useState<string | null>(null)
  const [dawaAddress, setDawaAddress] = useState<string | null>(null)
  const { palette } = useTheme()

  const form = useForm<AddressFormInput>()

  const data: DisclaimerTexts = useStaticQuery(graphql`
    {
      HAP: contentfulModuleComponentBundle(contentful_id: { eq: "5Y9WXKjsHRRmeMc1yoPz7N" }) {
        resources {
          ... on ContentfulComponentTextResource {
            content {
              content
            }
            title
          }
        }
      }
      VOK: contentfulModuleComponentBundle(contentful_id: { eq: "2V1svdTNd7DJwgWEbaSigJ" }) {
        resources {
          ... on ContentfulComponentTextResource {
            content {
              content
            }
            title
          }
        }
      }
    }
  `)

  const formatDisclaimerSet = (data: DisclaimerTexts): formattedDisclaimerTexts => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        Object.fromEntries(value.resources.map(resource => [resource.title, resource.content.content])),
      ]),
    )
  }

  const disclaimerTexts = formatDisclaimerSet(data)

  const styles = {
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: `100%`,
      '& form': {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        paddingTop: '20px',
        width: '100%',
      },
    },
    noresults: {
      maxWidth: '800px',
      marginTop: '100px',
    },
    headline: {
      marginBottom: '10px',
    },
    spinner: {
      marginTop: '20px',
    },
  }

  const SearchTabStyle = styled('div')(({ theme }) => ({
    maxWidth: '480px',
    width: '100%',
    margin: '0 auto',
    marginBottom: 0,
    background: palette.common.white,
    borderRadius: '7px',
    '& > div': {
      padding: 0,
      '.MuiInputAdornment-positionStart': {
        paddingLeft: '9px',
      },
    },
  }))

  const handleSelect = async (address: IAddress | null) => {
    setAddress(address)
    setOpen(true)
    if (!address) {
      // when field is cleared using the X in the input field
      setExcludeSimilar(false)
      setOpen(false)
      setView(SEARCH)
      setProducts([])
    }

    // for selecting city, street etc before final address
    const setCaret = (value: number) => {
      if (inputRef?.current) {
        setTimeout(() => {
          inputRef?.current?.focus()
          inputRef?.current?.setSelectionRange(value, value)
        }, 1)
      }
    }

    if (address?.street) {
      setExcludeSimilar(true)
      if (address?.forslagstekst) setCaret(address?.forslagstekst?.length)
    }
    if (address?.city) {
      // sets the position of the keyboard caret, to help user write the full address.
      if (address?.forslagstekst) {
        const caretPostion = address.forslagstekst.length - `${address.postalCode} ${address.city}`.length - 2
        setCaret(caretPostion)
      }
    }
    if (address?.madId) {
      // for selecting the final address
      if (address?.forslagstekst) setCaret(address.forslagstekst.length)
      setOpen(false)
      getAddressDataAndUpdateProducts(address?.madId, addressType, address?.forslagstekst)
    }
  }

  const getSearchString = (value: string) => {
    return `${LINEVISITATION_ENDPOINT}/autocomplete/?searchTerm=${value}&excludeSimilar=${String(excludeSimilar)}`
  }

  const getProductTemplateFromTechnology = (technology: Technology): IProduct => {
    return {
      ...(priceComparisonModule.products.find((el: IProduct) => el.technology === technology) ||
        priceComparisonModule.products[0]),
    }
  }

  const getQuantityDetails = (technology: Technology) => {
    switch (technology) {
      case 'FIBER PLUS':
        return 'Gbit'
      default:
        return 'Mbit'
    }
  }

  const getAddressDataAndUpdateProducts = async (madId: string, addressType: string, addressText?: string) => {
    if (dawaAddress !== madId) setDawaValue(null)
    const url = `${LINEVISITATION_ENDPOINT}/addressLookup?madId=${madId}&addressType=${addressType}`
    setView(LOADING)
    try {
      const productsRes = await fetch(url)
      const productsArray: ILinevisitationProduct[] = await productsRes.json()
      const displayProducts = productsArray.map(product => {
        const productTemplate = getProductTemplateFromTechnology(product.technology)
        productTemplate.technology = product.technology // disclaimerText function needs to know the technology
        productTemplate.productQuantity = product.speed
        productTemplate.productQuantityDetails = getQuantityDetails(product.technology)
        productTemplate.price = `${product.price} kr.`
        productTemplate.preFilledMessage = `Jeg ønsker at høre mere om: ${productTemplate.productHeadline}, til ${addressText}`
        return productTemplate
      })
      displayProducts.forEach((el: IProduct) => (el.attentionText = ''))

      const hasOnly5G = displayProducts.every(product => product.technology === 'MOBILE_BB')

      // Find the last FIBER product, otherwise the only MOBILE_BB product if other tech exists
      const popularIndex = displayProducts.reduce((acc, product, index) => {
        if (product.technology === 'FIBER') return index
        if (!hasOnly5G && product.technology === 'MOBILE_BB' && acc === -1) return index
        return acc
      }, -1)

      // Set attentionText for the most popular product
      if (popularIndex !== -1) {
        displayProducts[popularIndex].attentionText = 'Mest populær'
      }

      // set alternateOffers
      displayProducts.forEach(product => {
        if (product.technology === 'FIBER PLUS') product.alternateOffers = 'Du kan også overveje'
      })

      const firstProductTechnology = displayProducts[0].productHeadline
      const productsAreSameTechnology =
        displayProducts.filter((product: IProduct) => product.productHeadline === firstProductTechnology).length ===
        displayProducts.length
      if (productsAreSameTechnology && displayProducts[0].features) {
        priceComparisonModule.sharedFeatures = {
          sharedFeatureItems: displayProducts[0].features,
          sharedFeaturesTitle: 'Uanset hvilken hastighed, I vælger, får I:',
          sharedFeaturesAreStacked: false,
        }
        displayProducts.forEach((el: IProduct) => (el.features = undefined))
      } else {
        priceComparisonModule.sharedFeatures = undefined
      }
      priceComparisonModule.priceDesclaimerText.priceDesclaimerText =
        getDisclaimerTextAndAppendAsterisks(displayProducts)

      setView(productsArray.length ? RESULTS : NORESULTS)
      setProducts(displayProducts)
    } catch (err) {
      setView(NORESULTS)
    }
  }

  const getDisclaimerTextAndAppendAsterisks = (productsArray: IProduct[]) => {
    let disclaimerText = ''
    const technologiesDisclaimered: Record<string, boolean> = {}
    let disclaimerStars = '*'

    productsArray.forEach(product => {
      if (product.technology && !technologiesDisclaimered[product.technology]) {
        technologiesDisclaimered[product.technology] = true

        //Determine which disclaimer set to return
        const disclaimerSet = isHap ? disclaimerTexts.HAP : disclaimerTexts.VOK

        if (product.technology !== 'FIBER PLUS' && disclaimerSet[product.technology]) {
          disclaimerText = disclaimerText + disclaimerStars + disclaimerSet[product.technology] + '\n'
          productsArray.forEach((el: any) => {
            if (el.technology == product.technology) {
              el.priceDetails = el.priceDetails + disclaimerStars
            }
          })
          disclaimerStars = disclaimerStars + '*'
        }
      }
    })
    return disclaimerText
  }

  const getDawaIdUrl = (id: string) => `https://dawa.aws.dk/adresser/${id}?struktur=mini`

  const getDawaAddress = async (id: string) => {
    try {
      // Fetch DAWA data
      const dawaData = await fetch(getDawaIdUrl(id))
      if (!dawaData.ok) {
        throw new Error(`DAWA API request failed with status ${dawaData.status}`)
      }
      const dawaRes = await dawaData.json()
      setDawaValue(dawaRes.betegnelse)
    } catch (err) {
      console.error('Error fetching address data:', err)
      return null
    }
  }
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const dawaId = queryParams.get('dawa_id')
    if (!dawaId) return
    const formattedDawaId = dawaId?.replace(/-/g, '').toUpperCase()
    if (!formattedDawaId) return
    setDawaAddress(formattedDawaId)
    getDawaAddress(dawaId)
    getAddressDataAndUpdateProducts(formattedDawaId, addressType)
  }, [])

  const reset = () => {
    setView(SEARCH)
    setExcludeSimilar(false)
    setAddress(null)
  }

  return (
    <Box sx={styles.root} id="linevisitation">
      <Typography sx={styles.headline} variant="h5" component="h2">
        {products.length ? resultsTitle : searchTitle}
      </Typography>
      <FormProvider {...form}>
        <form onChange={e => (e?.target as HTMLInputElement)?.value?.length < 3 && reset()}>
          <Controller
            name="dawa"
            control={form.control}
            render={field => (
              <SearchTabStyle>
                <AutocompleteBase<IAddress>
                  {...field}
                  autoSelect={false}
                  data-testid="linevisitation-autocomplete"
                  fieldType={FieldType.ADDRESS}
                  autoComplete={true}
                  getOptionLabel={(option: IAddress) => option.forslagstekst || ''}
                  getOptionSelected={(value: IAddress, option: IAddress) => value.madId === option.madId}
                  inputRef={inputRef}
                  modelData={data => data}
                  name={searchTitle}
                  onChange={handleSelect}
                  open={open}
                  required={true}
                  urlBuilder={getSearchString}
                  label=""
                  placeholder={searchFieldLabel}
                />
              </SearchTabStyle>
            )}
          />
        </form>
      </FormProvider>
      {view === RESULTS && dawaValue && (
        <Box sx={{ paddingTop: '20px' }}>
          <Typography sx={{ textAlign: 'center', fontWeight: '500' }} component={'p'} variant="h5">
            Produkter vises for adressen
          </Typography>
          <Typography sx={{ textAlign: 'center', fontWeight: '700' }} component={'p'} variant="h5">
            {dawaValue}
          </Typography>
        </Box>
      )}
      {view === RESULTS && (
        <PriceComparison
          {...priceComparisonModule}
          products={products}
          noofSlide={noofSlide}
          trackingIdModule="linevisitation"
        />
      )}
      {view === NORESULTS && (
        <Box sx={styles.noresults}>
          <RichText {...noResultsExplanation} references={[]} />
        </Box>
      )}
      {view === LOADING && <CircularProgress sx={styles.spinner} />}
    </Box>
  )
}

export default Linevisitation
